/**
 * reminderParser — figures out whether a line of text looks like a
 * reminder, and if so, extracts the structured pieces (date, recurrence,
 * end conditions, the human-readable parts).
 *
 * Two detection paths:
 *
 *   (a) DATED — chrono parses a date phrase at the START of the line
 *       (within first ~12 chars), and 1+ word follows.
 *       Examples that pass:
 *         - "Wed 3pm gym"
 *         - "Friday call mom"
 *         - "tomorrow morning send invoice"
 *       Examples that fail:
 *         - "Wed"  (no follow-up word)
 *         - "we discussed Wed at 3" (date not at start)
 *         - "3pm" (no follow-up word)
 *
 *   (b) INTENT MARKER — line starts with "remember to ", "remind me to ",
 *       "reminder:", "reminder ", "todo:", "todo " (case-insensitive),
 *       and 1+ word follows. Treated as UNDATED.
 *
 * Recurrence detection sits between the two: a recurrence phrase
 * ("every Mon Wed Fri", "every first Tuesday of the month") at the
 * start of the line counts as a reminder on its own — even without
 * an explicit date — because we can compute the first occurrence
 * from the rule.
 *
 * Recurrence rule SCHEMA (RFC 5545–shaped, but JSON-ergonomic):
 *
 *   {
 *     freq:       "DAILY" | "WEEKLY" | "MONTHLY" | "YEARLY",
 *     interval:   number (default 1),
 *     byDay?:     ["MO","TU","WE","TH","FR","SA","SU"]  (subset)
 *     byMonthDay?: number   // e.g. 1 for "1st of the month"
 *     bySetPos?:   number   // 1 for "first ...", -1 for "last ..."
 *     count?:      number   // end after N occurrences
 *     until?:      ISO date string ("YYYY-MM-DD")  // end on/before
 *     occurrencesFired?: number  // bookkeeping, set by fire engine
 *   }
 *
 * Recognised inline patterns (handled here):
 *   - "every day"              -> DAILY, interval 1
 *   - "every 3 days"           -> DAILY, interval 3
 *   - "daily"                  -> DAILY
 *   - "every week"             -> WEEKLY
 *   - "weekly"                 -> WEEKLY
 *   - "every 2 weeks"          -> WEEKLY, interval 2
 *   - "every monday"           -> WEEKLY, byDay [MO]
 *   - "every Mon Wed Fri"      -> WEEKLY, byDay [MO,WE,FR]
 *   - "every Monday and Wednesday" -> same with and/comma/&/slash
 *   - "every 2 weeks on Tuesday"   -> WEEKLY, interval 2, byDay [TU]
 *   - "every weekday"          -> WEEKLY, byDay [MO,TU,WE,TH,FR]
 *   - "every first Tuesday of the month" -> MONTHLY, bySetPos 1, byDay [TU]
 *   - "every last Friday of the month"   -> MONTHLY, bySetPos -1, byDay [FR]
 *   - "every 15th of the month" / "every 1st" -> MONTHLY, byMonthDay
 *   - "every month" / "monthly"            -> MONTHLY
 *   - "every year" / "yearly" / "annually" -> YEARLY
 *
 * End-condition suffixes (immediately after the recurrence + optional
 * date phrase, before the task text):
 *   - "until <chrono-parseable date>"   -> until = YYYY-MM-DD
 *   - "for N occurrences|times|weeks|days|months|years" -> count = N
 *
 * Legacy stored shapes ({ kind: 'weekly', byDay: 'mon' }) are read
 * via `normalizeRecurrence(rec)` which always returns the new shape.
 * Writes go through the new shape directly.
 *
 * Note on chrono-node: this module imports it but does NOT crash if it's
 * missing (e.g. before `npm install chrono-node` has run). It falls back
 * to "no detection" so the rest of the app keeps working.
 */

let chrono = null;
try {
  // eslint-disable-next-line global-require
  chrono = require('chrono-node');
} catch (e) {
  // chrono-node not installed yet — module loads but parses nothing.
}

// How far into a line we'll look for a leading date phrase when NO
// recurrence prefix is present. With a recurrence prefix the date
// phrase must follow immediately after.
const LEADING_DATE_WINDOW = 12;

/**
 * isLogMarkedLine — true if the line's first non-whitespace char is
 * `@`-followed-by-non-whitespace, i.e. the user is marking it as a
 * log entry.
 */
export function isLogMarkedLine(text) {
  return /^\s*@\S/.test(text || '');
}

const INTENT_MARKERS = [
  /^remember\s+to\s+/i,
  /^remind\s+me\s+to\s+/i,
  /^reminder\s*:\s*/i,
  /^reminder\s+/i,
  /^todo\s*:\s*/i,
  /^todo\s+/i,
];

// ─────────────────────────────────────────────────────────────────────
// Recurrence schema helpers
// ─────────────────────────────────────────────────────────────────────

const DOW_SHORT = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'];
const DOW_NUMS = { MO: 1, TU: 2, WE: 3, TH: 4, FR: 5, SA: 6, SU: 0 };
const DOW_LABELS_LONG = { MO: 'Monday', TU: 'Tuesday', WE: 'Wednesday', TH: 'Thursday', FR: 'Friday', SA: 'Saturday', SU: 'Sunday' };
const DOW_LABELS_SHORT = { MO: 'Mon', TU: 'Tue', WE: 'Wed', TH: 'Thu', FR: 'Fri', SA: 'Sat', SU: 'Sun' };
// Walter 2026-05-28: `mo` added for Monday — the only weekday that was
// missing its 2-letter shortform. The others all already accepted
// `tu/we/th/fr/sa/su`. parseDayList uses these patterns to recognise
// individual day tokens within a list ("every Mo and Fr").
const DOW_TOKENS = [
  ['MO', /^(mondays?|mon|mo)$/i],
  ['TU', /^(tuesdays?|tues?|tu)$/i],
  ['WE', /^(wednesdays?|weds?|we)$/i],
  ['TH', /^(thursdays?|thurs?|thu|th)$/i],
  ['FR', /^(fridays?|fri|fr)$/i],
  ['SA', /^(saturdays?|sats?|sa)$/i],
  ['SU', /^(sundays?|suns?|su)$/i],
];

function tokenToShortDow(tok) {
  const t = (tok || '').trim();
  for (const [short, re] of DOW_TOKENS) {
    if (re.test(t)) return short;
  }
  return null;
}

const ORDINAL_TOKENS = {
  first: 1, '1st': 1,
  second: 2, '2nd': 2,
  third: 3, '3rd': 3,
  fourth: 4, '4th': 4,
  fifth: 5, '5th': 5,
  last: -1,
};

/**
 * normalizeRecurrence — convert any stored recurrence shape (legacy
 * `{kind, interval, byDay: 'mon'}` or new `{freq, ...}`) to the new
 * canonical schema. Returns null when input is missing/invalid.
 *
 * All downstream code (formatRecurrenceLabel, agenda, fire engine,
 * picker) should call this first so it only ever has to reason about
 * one shape.
 */
export function normalizeRecurrence(rec) {
  if (!rec) return null;
  if (rec.freq) {
    const out = {
      freq: rec.freq,
      interval: Math.max(1, rec.interval || 1),
    };
    if (Array.isArray(rec.byDay) && rec.byDay.length > 0) {
      out.byDay = rec.byDay.filter(d => DOW_SHORT.includes(d));
      if (out.byDay.length === 0) delete out.byDay;
    }
    // Walter 2026-05-28 (#9): byMonthDay now accepts a single number
    // OR an array of numbers (1-31). Single number stays as scalar for
    // back-compat with existing Firestore docs. Array is preserved as
    // sorted+deduped int[].
    if (typeof rec.byMonthDay === 'number') {
      out.byMonthDay = rec.byMonthDay;
    } else if (Array.isArray(rec.byMonthDay)) {
      const arr = [...new Set(
        rec.byMonthDay.filter(n => typeof n === 'number' && n >= 1 && n <= 31)
      )].sort((a, b) => a - b);
      if (arr.length === 1) out.byMonthDay = arr[0];
      else if (arr.length > 1) out.byMonthDay = arr;
    }
    if (typeof rec.bySetPos === 'number') out.bySetPos = rec.bySetPos;
    if (typeof rec.count === 'number') out.count = rec.count;
    if (typeof rec.until === 'string') out.until = rec.until;
    if (typeof rec.occurrencesFired === 'number') out.occurrencesFired = rec.occurrencesFired;
    return out;
  }
  // Legacy `{kind, interval, byDay: 'mon'}` shape.
  const interval = Math.max(1, rec.interval || 1);
  switch (rec.kind) {
    case 'daily':
      return { freq: 'DAILY', interval };
    case 'weekday':
      return { freq: 'WEEKLY', interval: 1, byDay: ['MO','TU','WE','TH','FR'] };
    case 'weekly': {
      const out = { freq: 'WEEKLY', interval };
      if (typeof rec.byDay === 'string') {
        const short = legacyDowToShort(rec.byDay);
        if (short) out.byDay = [short];
      } else if (Array.isArray(rec.byDay)) {
        const arr = rec.byDay.map(d => legacyDowToShort(d) || d).filter(d => DOW_SHORT.includes(d));
        if (arr.length > 0) out.byDay = arr;
      }
      return out;
    }
    case 'monthly': {
      const out = { freq: 'MONTHLY', interval };
      if (typeof rec.byMonthDay === 'number') out.byMonthDay = rec.byMonthDay;
      return out;
    }
    case 'yearly':
      return { freq: 'YEARLY', interval };
    default:
      return null;
  }
}

function legacyDowToShort(s) {
  const k = (s || '').toLowerCase().slice(0, 3);
  const map = { mon: 'MO', tue: 'TU', wed: 'WE', thu: 'TH', fri: 'FR', sat: 'SA', sun: 'SU' };
  return map[k] || null;
}

// ─────────────────────────────────────────────────────────────────────
// Parser: match a leading "every ..." recurrence phrase
// ─────────────────────────────────────────────────────────────────────

/**
 * A LIST OF DAYS OF THE MONTH — "4th, 14th and 24th". Shared by the
 * "every …" and "monthly / every month …" patterns below.
 *
 * Walter 2026-09-21: he typed "every 4th 14th and 24th pay bills" and got a
 * reminder on the 4th only, "14th and 24th" left in the task text. The list
 * accepted only , / and / & / slash between days. Now also:
 *   - a plain SPACE — but only when the next day carries its st/nd/rd/th
 *     ending, so "every 4th 2pm" and "every 4th 10 dollars" can't turn the
 *     number into a second day;
 *   - ", and" / ", &" (the serial comma) — which used to stop the list one
 *     day short and SILENTLY drop the last day: "every 4th, 14th, and 24th"
 *     came out as the 4th and 14th.
 */
// Walter 2026-09-26: words that introduce the START DATE of a recurring rule.
// "every Mon and Thu starting 10/1 at 9am" is the canonical form we write, so
// it must also be a form we read.
const ANCHOR_WORD_RE = /^(?:starting|beginning|as of|from)(?:\s+(?:on|from))?\s+/i;

const DAY_SRC = '\\d{1,2}(?:st|nd|rd|th)?';
const DAY_LIST_NEXT_SRC =
  `\\s*(?:,\\s*(?:and\\b|&)?|\\band\\b|&|\\/)\\s*${DAY_SRC}` // , / and / & / slash / ", and"
  + '|\\s+\\d{1,2}(?:st|nd|rd|th)\\b';                        // space — ordinal suffix required
const DAY_LIST_SRC = `${DAY_SRC}(?:${DAY_LIST_NEXT_SRC})*`;

/**
 * matchRecurrencePrefix(trimmed)
 *
 * Tries to extract a recurrence rule from the start of `trimmed`.
 * Returns either null (no rule found) or:
 *   {
 *     recurrence: { freq, interval, byDay?, byMonthDay?, bySetPos? },
 *     matchStart: 0,
 *     matchEnd:   number,  // exclusive
 *     keepDayName: bool,   // if true, leave a day-name word in place
 *                          // so chrono can use it to compute the date
 *     dayChronoOffset: number  // for keepDayName, where chrono should
 *                              // start parsing (matchStart + "every "
 *                              // length).
 *   }
 *
 * The list is tried IN ORDER — most specific patterns first.
 */
function matchRecurrencePrefix(trimmed) {
  // 1) Nth-weekday-of-month: "every first Tuesday of the month"
  {
    const re = /^every\s+(first|second|third|fourth|fifth|last|1st|2nd|3rd|4th|5th)\s+(mondays?|tuesdays?|tues?|wednesdays?|weds?|thursdays?|thurs?|thu|fridays?|fri|saturdays?|sats?|sundays?|suns?)(?:\s+of(?:\s+the)?(?:\s+month)?)?\b/i;
    const m = trimmed.match(re);
    if (m) {
      const pos = ORDINAL_TOKENS[m[1].toLowerCase()];
      const dow = tokenToShortDow(m[2]);
      if (pos != null && dow) {
        return {
          recurrence: { freq: 'MONTHLY', interval: 1, bySetPos: pos, byDay: [dow] },
          matchStart: 0,
          matchEnd: m[0].length,
          keepDayName: false,
        };
      }
    }
  }

  // 2) "every Nth of the month" / "every 15th of the month"
  //    Walter 2026-05-28 (#9): extended to match a LIST of days —
  //    "every 1st and 15th", "every 1st, 15th and 30th", etc. The
  //    "of the month" suffix is optional when there's >=2 days (the
  //    phrasing is unambiguous; a bare list of ordinals doesn't read
  //    as anything else). For single days the suffix is still
  //    required to distinguish "every 15" (meaningless without
  //    context) from "every 15th of the month".
  {
    const re = new RegExp(`^every\\s+(${DAY_LIST_SRC})(?:\\s+of(?:\\s+the)?(?:\\s+month)?)?\\b`, 'i');
    const m = trimmed.match(re);
    if (m) {
      // Pull every number out of the captured list.
      const nums = [];
      const numRe = /\d{1,2}/g;
      let nm;
      while ((nm = numRe.exec(m[1])) !== null) {
        const n = parseInt(nm[0], 10);
        if (n >= 1 && n <= 31) nums.push(n);
      }
      const dedup = [...new Set(nums)].sort((a, b) => a - b);
      const hasMonthSuffix = /of(\s+the)?\s+month|of\s+each\s+month|\bof\s+month\b/i.test(m[0]);
      const isMultiDay = dedup.length > 1;
      // Walter 2026-05-28: accept a single ordinal-suffixed day even
      // WITHOUT "of the month" — "every 1st", "every 15th". The
      // ordinal suffix (st/nd/rd/th) is a strong day-of-month signal,
      // so requiring "of the month" was too strict (Walter typed
      // "every 1st call larry" and it fell through to undated). A
      // BARE number ("every 15") stays guarded — too ambiguous (could
      // be "every 15 minutes").
      const hasOrdinalSuffix = /\d(?:st|nd|rd|th)/i.test(m[1]);
      // Guard: don't treat "every 2nd week" / "every 3rd day" as a
      // day-of-month — those are interval phrasings. If a time-unit
      // word immediately follows the matched ordinal, bail so the
      // every-N-weeks / every-other-X patterns (or none) handle it.
      const remainderAfter = trimmed.slice(m[0].length);
      const followedByUnit = /^\s*(days?|weeks?|months?|years?|quarters?|weekday|business)\b/i.test(remainderAfter);
      if (dedup.length > 0 && !followedByUnit && (isMultiDay || hasMonthSuffix || hasOrdinalSuffix)) {
        return {
          recurrence: {
            freq: 'MONTHLY',
            interval: 1,
            byMonthDay: dedup.length === 1 ? dedup[0] : dedup,
          },
          matchStart: 0,
          matchEnd: m[0].length,
          keepDayName: false,
        };
      }
    }
  }

  // 3) "every weekday" (Mon-Fri)
  {
    const re = /^every\s+weekday(s)?\b/i;
    const m = trimmed.match(re);
    if (m) {
      return {
        recurrence: { freq: 'WEEKLY', interval: 1, byDay: ['MO','TU','WE','TH','FR'] },
        matchStart: 0,
        matchEnd: m[0].length,
        keepDayName: false,
      };
    }
  }

  // 4) "every N weeks on <day-list>" or "every N weeks"
  {
    const re = /^every\s+(\d+)\s+weeks?(?:\s+on\s+([a-zA-Z]+(?:\s*(?:,|and|&|\/)\s*[a-zA-Z]+)*(?:\s+[a-zA-Z]+(?:\s*(?:,|and|&|\/)\s*[a-zA-Z]+)*)*))?\b/i;
    const m = trimmed.match(re);
    if (m) {
      const interval = parseInt(m[1], 10) || 1;
      const dowList = m[2] ? parseDayList(m[2]) : null;
      const rec = { freq: 'WEEKLY', interval };
      if (dowList && dowList.length > 0) rec.byDay = dowList;
      return {
        recurrence: rec,
        matchStart: 0,
        matchEnd: m[0].length,
        keepDayName: false,
      };
    }
  }

  // 4b) "every other <unit-or-day>" — interval=2 (NL_INPUT_AUDIT §5f).
  //     "every other day/week/month/year" and "every other Wednesday".
  {
    const m = trimmed.match(/^every\s+other\s+/i);
    if (m) {
      const tail = trimmed.slice(m[0].length);
      const unitM = tail.match(/^(day|week|month|year)s?\b/i);
      if (unitM) {
        const u = unitM[1].toLowerCase();
        const freq = u === 'day' ? 'DAILY' : u === 'week' ? 'WEEKLY'
          : u === 'month' ? 'MONTHLY' : 'YEARLY';
        return {
          recurrence: { freq, interval: 2 },
          matchStart: 0,
          matchEnd: m[0].length + unitM[0].length,
          keepDayName: false,
        };
      }
      const wordM = tail.match(/^([a-zA-Z]{2,9})/);
      if (wordM) {
        const dow = tokenToShortDow(wordM[1]);
        if (dow) {
          // keepDayName: leave the day word for chrono so the first
          // occurrence is computed; dayChronoOffset starts chrono just
          // after "every other ".
          return {
            recurrence: { freq: 'WEEKLY', interval: 2, byDay: [dow] },
            matchStart: 0,
            matchEnd: m[0].length,
            keepDayName: true,
            dayChronoOffset: m[0].length,
          };
        }
      }
    }
  }

  // 4c) "every weekend" is handled in the pre-processor (rewritten to
  //     "every Sat and Sun" before this matcher runs) so the window
  //     rewrite for bare "weekend" can't eat it. Pattern 5 below then
  //     parses the day-list. See buildRewriteRules.

  // 4d) "quarterly" / "every quarter" → MONTHLY interval 3
  //     (NL_INPUT_AUDIT §9 Q4 — coerced to MONTHLY×3, no schema change).
  {
    const m = trimmed.match(/^(?:quarterly|every\s+quarter)\b/i);
    if (m) {
      return {
        recurrence: { freq: 'MONTHLY', interval: 3 },
        matchStart: 0,
        matchEnd: m[0].length,
        keepDayName: false,
      };
    }
  }

  // 5) "every <day-list>" — single or multiple days
  //    e.g. "every Monday", "every Mon Wed Fri", "every Monday and Wednesday"
  //
  //    Walter 2026-05-11: accept the LONGEST PREFIX of valid day
  //    tokens. Previously the pattern's greedy regex captured trailing
  //    non-day words ("every Monday and Wednesday tester" → captured
  //    "Monday and Wednesday tester"); parseDayList saw "tester" and
  //    returned null, dropping the WHOLE recurrence. Now we walk
  //    token-by-token, accept day tokens, and stop at the first
  //    non-day word — task text after the day list is preserved.
  //
  //    Single-day case keeps the day name visible to chrono (so
  //    "every Monday at 3pm" still parses the time). Multi-day strips
  //    the whole phrase and computes the first occurrence from byDay.
  {
    const everyM = trimmed.match(/^every\s+/i);
    if (everyM) {
      const afterEvery = everyM[0].length;
      const tail = trimmed.slice(afterEvery);
      // Walk: word, optional separator, word, ... Accept while words
      // are day tokens. Stop at first non-day or no separator-or-space.
      const days = [];
      let consumed = 0;
      while (true) {
        const wordM = tail.slice(consumed).match(/^([a-zA-Z]{2,9})/i);
        if (!wordM) break;
        const short = tokenToShortDow(wordM[1]);
        if (!short) break;
        if (!days.includes(short)) days.push(short);
        const tentative = consumed + wordM[0].length;
        // Look ahead for a separator that COULD lead to another day.
        const sepM = tail.slice(tentative).match(/^(\s*(?:,|and|&|\/)\s+|\s+)/i);
        if (sepM) {
          // Try the next word — if it's a day, take this separator.
          // If not, stop AT this point (don't consume the trailing
          // separator, so task text stays clean).
          const nextWordM = tail.slice(tentative + sepM[0].length).match(/^([a-zA-Z]{2,9})/i);
          if (nextWordM && tokenToShortDow(nextWordM[1])) {
            consumed = tentative + sepM[0].length;
            continue;
          }
          consumed = tentative;
          break;
        }
        consumed = tentative;
        break;
      }
      if (days.length > 0) {
        const matchEnd = afterEvery + consumed;
        if (days.length === 1) {
          return {
            recurrence: { freq: 'WEEKLY', interval: 1, byDay: days },
            matchStart: 0,
            matchEnd,
            keepDayName: true,
            dayChronoOffset: afterEvery,
          };
        }
        return {
          recurrence: { freq: 'WEEKLY', interval: 1, byDay: days },
          matchStart: 0,
          matchEnd,
          keepDayName: false,
        };
      }
    }
  }

  // 5b) "monthly <day-of-month>" — e.g. "monthly 15th", "monthly on the
  //     3rd", "every month the 1st". Walter 2026-07-04: a bare ordinal
  //     after a monthly keyword reads as the day of month, but chrono
  //     won't grab a lone "15th" as a date (so it fell through to the
  //     plain "monthly" keyword and left "15th" in the task text). Anchor
  //     it explicitly via byMonthDay — the same shape pattern 2 produces
  //     for "every 15th of the month". Requires the st/nd/rd/th suffix so
  //     a bare "monthly 15" (ambiguous) still stays out.
  {
    // Walter 2026-09-21: a LIST works here too ("every month on the 4th, 14th
    // and 24th"). The first day keeps its required ordinal suffix, so a bare
    // "monthly 15" still stays out; the rest follow DAY_LIST_SRC's rules.
    const re = new RegExp(`^(?:monthly|every\\s+month)\\s+(?:on\\s+)?(?:the\\s+)?(\\d{1,2}(?:st|nd|rd|th)(?:${DAY_LIST_NEXT_SRC})*)\\b`, 'i');
    const m = trimmed.match(re);
    if (m) {
      const days = [...new Set((m[1].match(/\d{1,2}/g) || [])
        .map(n => parseInt(n, 10))
        .filter(n => n >= 1 && n <= 31))].sort((a, b) => a - b);
      if (days.length > 0) {
        return {
          recurrence: { freq: 'MONTHLY', interval: 1, byMonthDay: days.length === 1 ? days[0] : days },
          matchStart: 0,
          matchEnd: m[0].length,
          keepDayName: false,
        };
      }
    }
  }

  // 6) Plain keyword recurrences (no by-day)
  const PLAIN = [
    { re: /^every\s+day\b/i,           rec: { freq: 'DAILY',   interval: 1 } },
    { re: /^daily\b/i,                 rec: { freq: 'DAILY',   interval: 1 } },
    { re: /^every\s+(\d+)\s+days?\b/i, rec: { freq: 'DAILY' }, intervalGroup: 1 },
    { re: /^every\s+week\b/i,          rec: { freq: 'WEEKLY',  interval: 1 } },
    { re: /^weekly\b/i,                rec: { freq: 'WEEKLY',  interval: 1 } },
    { re: /^every\s+month\b/i,         rec: { freq: 'MONTHLY', interval: 1 } },
    { re: /^monthly\b/i,               rec: { freq: 'MONTHLY', interval: 1 } },
    { re: /^every\s+(\d+)\s+months?\b/i, rec: { freq: 'MONTHLY' }, intervalGroup: 1 },
    { re: /^every\s+year\b/i,          rec: { freq: 'YEARLY',  interval: 1 } },
    { re: /^yearly\b/i,                rec: { freq: 'YEARLY',  interval: 1 } },
    { re: /^annually\b/i,              rec: { freq: 'YEARLY',  interval: 1 } },
    // Walter 2026-07-04: "annual 7/1 do X" wasn't understood — only
    // "annually"/"yearly" were keywords, so "annual" fell through to a
    // one-off. `\b` after "annual" means this never shadows "annually".
    { re: /^annual\b/i,                rec: { freq: 'YEARLY',  interval: 1 } },
    { re: /^every\s+(\d+)\s+years?\b/i, rec: { freq: 'YEARLY' }, intervalGroup: 1 },
  ];
  for (const p of PLAIN) {
    const m = trimmed.match(p.re);
    if (!m) continue;
    const rec = { ...p.rec };
    if (p.intervalGroup) rec.interval = parseInt(m[p.intervalGroup], 10) || 1;
    if (!rec.interval) rec.interval = 1;
    return {
      recurrence: rec,
      matchStart: 0,
      matchEnd: m[0].length,
      keepDayName: false,
    };
  }

  return null;
}

// Parse a space- / comma- / and-separated list of weekday tokens.
// Returns array of "MO"|"TU"|... or null if any token doesn't parse
// as a weekday.
function parseDayList(s) {
  if (!s) return null;
  const tokens = s
    .split(/\s*(?:,|&|\/|\sand\s|\s+)\s*/i)
    .map(t => t.trim().toLowerCase())
    .filter(Boolean);
  const out = [];
  for (const t of tokens) {
    const short = tokenToShortDow(t);
    if (!short) return null; // any non-day token poisons the match
    if (!out.includes(short)) out.push(short);
  }
  return out.length > 0 ? out : null;
}

// ─────────────────────────────────────────────────────────────────────
// Parser: extract end-condition suffix ("until X", "for N units")
// ─────────────────────────────────────────────────────────────────────

/**
 * matchEndCondition(remaining)
 *
 * remaining = trimmed.slice(cursor) — text starting where we expect
 * the end-condition phrase (if any) to begin.
 *
 * Returns either null or:
 *   { partial: { until?: 'YYYY-MM-DD', count?: number },
 *     matchLength: number }   // chars consumed at start of `remaining`
 */
function matchEndCondition(remaining, now) {
  if (!remaining) return null;
  // "for N (occurrences|times|weeks|days|months|years)"
  const forM = remaining.match(/^for\s+(\d+)\s+(times|occurrences|weeks?|days?|months?|years?)\b/i);
  if (forM) {
    const n = parseInt(forM[1], 10);
    if (n > 0) return { partial: { count: n }, matchLength: forM[0].length };
  }
  // "until <chrono-parseable date>"
  const untilM = remaining.match(/^until\s+/i);
  if (untilM && chrono) {
    const after = remaining.slice(untilM[0].length);
    const results = chrono.parse(after, now, { forwardDate: true });
    if (results && results.length > 0 && results[0].index === 0) {
      const d = results[0].start.date();
      const iso = formatISODate(d);
      return { partial: { until: iso }, matchLength: untilM[0].length + results[0].text.length };
    }
  }
  return null;
}

function formatISODate(d) {
  if (!(d instanceof Date) || isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

// ─────────────────────────────────────────────────────────────────────
// Pre-processor — normalise listicity vocabulary before chrono
// ─────────────────────────────────────────────────────────────────────
//
// Walter 2026-05-21 (NL_INPUT_AUDIT.md §5/§8): chrono-node doesn't know
// listicity's productivity vocabulary — "eod", "tonight", "weekend",
// "30m", "biweekly", common typos. The pre-processor rewrites those
// tokens into plain phrases chrono (and matchRecurrencePrefix) DO
// understand, before either of them sees the line.
//
// The catch: a rewrite shifts character indices, and the capture flow
// (useReminderCapture.computeNewLineForConfirm) splices the ORIGINAL
// textarea line using parseReminderLine's replaceStart/replaceEnd. So
// preprocess() also returns mapToOriginal(idx, bias) — every index the
// parser reports is mapped back to original coordinates before the
// result leaves parseReminderLine. The parser itself works entirely in
// pre-processed space.
//
// Conservative by construction: a rule only fires on its own regex;
// unrecognised text (the task description) passes through byte-for-byte,
// so a rewrite can never corrupt the task words.

// Default hours that vague time-of-day words resolve to. NL_INPUT_AUDIT
// §9 v1 seed values — "configurable later"; kept as module constants
// until there's a Settings surface for them.
const ANCHOR = Object.freeze({
  morning: 9, afternoon: 13, evening: 18, night: 20,
  eod: 17, bod: 9, midMorning: 10, midAfternoon: 15, lateAfternoon: 17,
});
const LATER_OFFSET_HOURS = 3;   // "later" / "later today" → now + 3h
const WEEKEND_DOW = 6;          // Saturday (0=Sun .. 6=Sat)

function pad2(n) { return String(n).padStart(2, '0'); }

// "h:mmam"/"h:mmpm" from a 24h hour + optional minute.
function clockStr(hour, minute) {
  let h = hour % 12;
  if (h === 0) h = 12;
  return `${h}:${pad2(minute || 0)}${hour >= 12 ? 'pm' : 'am'}`;
}

// Explicit, year-qualified phrase chrono parses unambiguously — the
// year stops chrono's forwardDate from rolling a same-year date into
// next year. withTime=false → date only.
function chronoDateStr(d, withTime) {
  const base = `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
  return withTime ? `${base} ${clockStr(d.getHours(), d.getMinutes())}` : base;
}

function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}
function addHours(d, n) {
  const x = new Date(d);
  x.setHours(x.getHours() + n);
  return x;
}
// Next date whose weekday is `dow`. includeToday=true → today counts.
function upcomingDow(from, dow, includeToday) {
  const d = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  let delta = (dow - d.getDay() + 7) % 7;
  if (delta === 0 && !includeToday) delta = 7;
  d.setDate(d.getDate() + delta);
  return d;
}
function lastDayOfMonth(from) {
  return new Date(from.getFullYear(), from.getMonth() + 1, 0);
}
function firstOfNextMonth(from) {
  return new Date(from.getFullYear(), from.getMonth() + 1, 1);
}
function nextBusinessDay(from) {
  const d = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  do { d.setDate(d.getDate() + 1); } while (d.getDay() === 0 || d.getDay() === 6);
  return d;
}
function quarterStartDate(year, q) {
  return new Date(year, (q - 1) * 3, 1);  // q is 1..4
}
function nextQuarterStart(from) {
  const q = Math.floor(from.getMonth() / 3) + 1;
  return q === 4
    ? quarterStartDate(from.getFullYear() + 1, 1)
    : quarterStartDate(from.getFullYear(), q + 1);
}
// q1..q4 of the current year, or next year if that quarter already
// started (chrono-style "forward" resolution).
function resolveQuarter(from, q) {
  const start = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  let d = quarterStartDate(from.getFullYear(), q);
  if (d < start) d = quarterStartDate(from.getFullYear() + 1, q);
  return d;
}

/**
 * rewritePass(text, re, replacer) — one regex-replace pass. Returns
 * { text, map } where map(newIdx, bias) maps an index in the rewritten
 * text back to an index in `text`. `bias` ('start' | 'end') only
 * matters when newIdx lands inside a rewritten span.
 */
function rewritePass(text, re, replacer) {
  const edits = [];
  const out = text.replace(re, function () {
    const args = Array.prototype.slice.call(arguments);
    const match = args[0];
    const offset = args[args.length - 2];
    const replacement = String(replacer(args));
    edits.push({ oldStart: offset, oldLen: match.length, repl: replacement });
    return replacement;
  });
  // Pre-compute each edit's span in the NEW (rewritten) string.
  let delta = 0;
  const spans = edits.map((e) => {
    const newStart = e.oldStart + delta;
    const newEnd = newStart + e.repl.length;
    delta += e.repl.length - e.oldLen;
    return { oldStart: e.oldStart, oldEnd: e.oldStart + e.oldLen, newStart, newEnd };
  });
  const map = (newIdx, bias) => {
    let d = 0;
    for (const s of spans) {
      if (newIdx < s.newStart) return newIdx - d;            // before this edit
      if (newIdx < s.newEnd) return bias === 'end' ? s.oldEnd : s.oldStart;
      d += (s.newEnd - s.newStart) - (s.oldEnd - s.oldStart); // past this edit
    }
    return newIdx - d;
  };
  return { text: out, map };
}

// Ordered rewrite rules. Earlier rules run first; longer phrases are
// ordered before the shorter tokens they contain.
function buildRewriteRules(now) {
  const D = (d) => chronoDateStr(d, false);
  const DT = (d) => chronoDateStr(d, true);
  return [
    // Walter 2026-05-28: expand 2-letter weekday shortforms when they
    // appear in a context where they unambiguously mean a weekday —
    // (a) inside a recurrence prefix after "every " or "every other ",
    // and (b) at the start of a line right before a time-of-day. Both
    // contexts rule out ordinary English "we"/"th"/"mo" usage. The
    // expanded form is the full weekday name so chrono can resolve it.
    // For free-text usage outside these contexts ("we should call
    // mom"), the user types "Mon" / "Wed" / etc.
    {
      re: /(every(?:\s+other)?\s+)(mo|tu|we|th|fr|sa|su)\b/gi,
      fn: (a) => `${a[1]}${({mo:'Monday',tu:'Tuesday',we:'Wednesday',th:'Thursday',fr:'Friday',sa:'Saturday',su:'Sunday'})[a[2].toLowerCase()]}`,
    },
    {
      // Multi-day list inside a recurrence prefix: connector + 2-letter day.
      // Handles "every Mo and Fr", "every Mo, Tu, Fr", "every Mo & We".
      re: /(\s+(?:and|,|&|\/|\sor\s)\s*)(mo|tu|we|th|fr|sa|su)\b/gi,
      fn: (a) => `${a[1]}${({mo:'Monday',tu:'Tuesday',we:'Wednesday',th:'Thursday',fr:'Friday',sa:'Saturday',su:'Sunday'})[a[2].toLowerCase()]}`,
    },
    {
      // Walter 2026-05-28: line-start 2-letter weekday followed by ANY
      // task text → expand to the full weekday name. "mo call willie"
      // → "Monday call willie". Previously this only fired when a TIME
      // marker followed ("mo 3pm"), so "mo call willie" stayed literal
      // and didn't parse. EXCEPT "we" — that's also the English pronoun
      // ("we should call mom"), too collision-prone to expand on bare
      // text, so "we" is handled by the separate time-gated rule below.
      // mo/tu/th/fr/sa/su are not common English words, so a following
      // word is safely a weekday-prefixed reminder.
      //
      // Uses a `(?=\s)` LOOKAHEAD so the match is ONLY the 2-letter
      // token — we don't consume the following whitespace/char. (An
      // earlier version consumed `\s+\S` and re-emitted it, which
      // dropped a character through the rewrite index-map: "mo call"
      // → "Monday all".) The lookahead also blocks "moving"/"tuna"
      // (no whitespace right after the 2 letters → no match).
      // Walter 2026-06-09 (#1): lookahead allows a COLON too, so a
      // user-typed "Mo: Randy breakie" expands ("Monday: Randy …") and
      // dates correctly. Before, only a following space matched, so the
      // colon form fell through to no-match. Still blocks "moving"/"tuna"
      // (letter, not space/colon, follows).
      re: /^(mo|tu|th|fr|sa|su)(?=[\s:])/i,
      fn: (a) => ({mo:'Monday',tu:'Tuesday',th:'Thursday',fr:'Friday',sa:'Saturday',su:'Sunday'})[a[1].toLowerCase()],
    },
    {
      // "we" line-start: expand ONLY when a time marker follows, to
      // avoid mangling the pronoun ("we should call mom"). Users who
      // mean Wednesday with no time should type "Wed" / "Wednesday".
      //
      // Walter 2026-09-03: …OR when a COLON follows. "We:" is the one
      // shape that can't be the pronoun — nobody writes "we: should call
      // mom" — and it was the last gap in the weekday-colon family: Mo:,
      // Tu:, Th:, Fr:, Sa:, Su: all resolved (the rule above allows a
      // colon), and We: alone fell through. Worse than nothing: "We:
      // dentist 3pm" then took the 3pm as its DATE anchor and produced
      // "🔔 We: dentist 9/4 3pm" — the wrong day, with "We:" left as
      // literal text.
      re: /^we(?=:|\s+(?:at\s+|\d|noon\b|[ap]m\b))/i,
      fn: () => 'Wednesday',
    },
    {
      // Walter 2026-06-09: a leading dash-form numeric date ("6-7",
      // "6-7-27") → slash form so chrono parses it as a date. Only at
      // line start, and only when end-or-space follows — so a time range
      // "6-7pm" (no space before "pm") is left alone. Same char length as
      // the original, so the index map back to the original line is
      // unaffected.
      re: /^(\d{1,2})-(\d{1,2})(?:-(\d{2,4}))?(?=\s|$)/,
      fn: (a) => (a[3] ? `${a[1]}/${a[2]}/${a[3]}` : `${a[1]}/${a[2]}`),
    },

    // — typo / shorthand tolerance (§5g) —
    { re: /\b(tomorow|tommorow|tommorrow|tmrw|tmrrw|tmr)\b/gi, fn: () => 'tomorrow' },
    { re: /\btdy\b/gi, fn: () => 'today' },
    { re: /\b(wensday|wendsday|wednsday)\b/gi, fn: () => 'wednesday' },
    // Apostrophe year shorthand: "Apr 14 '27" → "Apr 14 2027" so chrono
    // reads the year (it otherwise leaves "'27" in the task text).
    { re: /(^|\s)'(\d{2})\b/g, fn: (a) => `${a[1]}20${a[2]}` },

    // — time math: "now + 4h" → "in 4 hours" (§5d) —
    { re: /\bnow\s*\+\s*(\d+)\s*h(?:ours?|rs?)?\b/gi, fn: (a) => `in ${a[1]} hours` },
    { re: /\bnow\s*\+\s*(\d+)\s*m(?:in(?:ute)?s?)?\b/gi, fn: (a) => `in ${a[1]} minutes` },

    // — duration shorthand, leading token only (§5d). Anchored at the
    //   start so "buy 2m rope" mid-task is never misread as a duration.
    {
      re: /^(\d+)\s*(h(?:ours?|rs?)?|m(?:in(?:ute)?s?)?|d(?:ays?)?|w(?:k|ks|eeks?)?)\b/i,
      fn: (a) => {
        const u = a[2][0].toLowerCase();
        const unit = u === 'h' ? 'hours' : u === 'm' ? 'minutes' : u === 'd' ? 'days' : 'weeks';
        return `in ${a[1]} ${unit}`;
      },
    },

    // — bare-hour heuristic: "at 5" → "at 5pm" for hours 1-7. Nobody
    //   schedules a reminder for 5 AM by typing "at 5"; chrono would
    //   otherwise read it as 05:00. Hours 8-12 stay chrono's default.
    //   `\b` after the digit already excludes "at 5pm"/"at 5am"/"at
    //   530"; the lookahead additionally excludes "at 5:30".
    { re: /\bat\s+([1-7])\b(?!:)/gi, fn: (a) => `at ${a[1]}pm` },

    // — recurrence aliases (§5f) — fed into matchRecurrencePrefix —
    { re: /\bbi[-\s]?weekly\b/gi, fn: () => 'every 2 weeks' },
    { re: /\bbi[-\s]?monthly\b/gi, fn: () => 'every 2 months' },
    // Drop the "on" in "every N weeks on <day> <task>" — the recurrence
    // matcher's day-list capture is greedy and would otherwise swallow
    // the task text. Without "on", pattern 4 stops at "every N weeks"
    // and chrono + the bare-weekly inference resolve the day.
    { re: /^(every\s+\d+\s+weeks?)\s+on\s+/i, fn: (a) => `${a[1]} ` },
    { re: /^every\s+workdays?\b/i, fn: () => 'every weekday' },
    // "every weekend" → an explicit day-list so the bare-"weekend"
    // window rewrite below can't consume it; pattern 5 in
    // matchRecurrencePrefix then reads byDay=[SA,SU].
    { re: /^every\s+weekends?\b/i, fn: () => 'every Sat and Sun' },
    { re: /^every\s+mornings?\b/i, fn: () => `every day ${clockStr(ANCHOR.morning)}` },
    { re: /^every\s+afternoons?\b/i, fn: () => `every day ${clockStr(ANCHOR.afternoon)}` },
    { re: /^every\s+evenings?\b/i, fn: () => `every day ${clockStr(ANCHOR.evening)}` },
    { re: /^every\s+nights?\b/i, fn: () => `every day ${clockStr(ANCHOR.night)}` },

    // — relative time-of-day vocabulary (§5a) — longer phrases first —
    { re: /\bfirst thing tomorrow\b/gi, fn: () => `tomorrow ${clockStr(ANCHOR.bod)}` },
    { re: /\bfirst thing\b/gi, fn: () => clockStr(ANCHOR.bod) },
    { re: /\blater today\b/gi, fn: () => DT(addHours(now, LATER_OFFSET_HOURS)) },
    { re: /\bthis morning\b/gi, fn: () => `today ${clockStr(ANCHOR.morning)}` },
    { re: /\bthis afternoon\b/gi, fn: () => `today ${clockStr(ANCHOR.afternoon)}` },
    { re: /\bthis evening\b/gi, fn: () => `today ${clockStr(ANCHOR.evening)}` },
    { re: /\btonight\b/gi, fn: () => `today ${clockStr(ANCHOR.night)}` },
    { re: /\blate afternoon\b/gi, fn: () => clockStr(ANCHOR.lateAfternoon) },
    { re: /\bmid[-\s]?afternoon\b/gi, fn: () => clockStr(ANCHOR.midAfternoon) },
    { re: /\bmid[-\s]?morning\b/gi, fn: () => clockStr(ANCHOR.midMorning) },

    // — business-day vocabulary (§5b) —
    { re: /\bend of day\b/gi, fn: () => clockStr(ANCHOR.eod) },
    { re: /\b(eod|cob)\b/gi, fn: () => clockStr(ANCHOR.eod) },
    { re: /\b(bod|sob)\b/gi, fn: () => clockStr(ANCHOR.bod) },
    { re: /\bnext (?:business day|workday)\b/gi, fn: () => D(nextBusinessDay(now)) },

    // — bare time-of-day words (§5a). "night" never matches inside
    //   "tonight" — no word boundary there — so order vs. tonight is
    //   irrelevant; tonight is already rewritten above regardless.
    { re: /\bmorning\b/gi, fn: () => clockStr(ANCHOR.morning) },
    { re: /\bafternoon\b/gi, fn: () => clockStr(ANCHOR.afternoon) },
    { re: /\bevening\b/gi, fn: () => clockStr(ANCHOR.evening) },
    { re: /\bnight\b/gi, fn: () => clockStr(ANCHOR.night) },

    // — approximate / window phrasing (§5c). "next X" before "this X"
    //   / bare "X" so the longer phrase wins. —
    { re: /\bnext weekend\b/gi, fn: () => D(addDays(upcomingDow(now, WEEKEND_DOW, true), 7)) },
    { re: /\b(?:this )?weekend\b/gi, fn: () => D(upcomingDow(now, WEEKEND_DOW, true)) },
    { re: /\bend of (?:the )?month\b/gi, fn: () => D(lastDayOfMonth(now)) },
    { re: /\bnext month\b/gi, fn: () => D(firstOfNextMonth(now)) },
    { re: /\bthis month\b/gi, fn: () => D(lastDayOfMonth(now)) },
    { re: /\b(?:end of (?:the )?week|eow)\b/gi, fn: () => D(upcomingDow(now, 5, true)) },
    { re: /\bnext quarter\b/gi, fn: () => D(nextQuarterStart(now)) },
    { re: /\bq([1-4])\b/gi, fn: (a) => D(resolveQuarter(now, parseInt(a[1], 10))) },
    { re: /\b(?:sometime )?next week\b/gi, fn: () => D(upcomingDow(now, 1, false)) },
    { re: /\bthis week\b/gi, fn: () => D(upcomingDow(now, 5, true)) },
    { re: /\blater\b/gi, fn: () => DT(addHours(now, LATER_OFFSET_HOURS)) },
  ];
}

/**
 * preprocess(text, now) — apply every rewrite rule in order, composing
 * the per-pass index maps. Returns { text, mapToOriginal }.
 */
function preprocess(text, now) {
  let cur = text;
  const maps = [];
  for (const rule of buildRewriteRules(now)) {
    const res = rewritePass(cur, rule.re, rule.fn);
    cur = res.text;
    maps.push(res.map);
  }
  // To map a final index back to original, undo passes in reverse.
  const mapToOriginal = (idx, bias) => {
    let i = idx;
    for (let k = maps.length - 1; k >= 0; k--) i = maps[k](i, bias);
    return i;
  };
  return { text: cur, mapToOriginal };
}

// ─────────────────────────────────────────────────────────────────────
// Public: parseReminderLine
// ─────────────────────────────────────────────────────────────────────

/**
 * parseReminderLine(text, opts?)
 *
 * Returns one of:
 *   { isReminder: false, kind: null, reason: 'no-match' | 'date-not-at-start'
 *     | 'no-text-after-date' | 'chrono-missing' | 'empty' }
 *  OR
 *   { isReminder: true, kind: 'dated',
 *     parsedDate: Date,
 *     dateText: string,        // chrono's matched phrase
 *     dateIndex: number,       // index of dateText within trimmed input
 *     hasCertainTime: boolean,
 *     restText: string,        // line minus date+recurrence+end-cond
 *     recurrence: { freq, ... } | null,
 *     confidence: 'high' | 'medium' | 'low',
 *     replaceStart: number,    // index in trimmed where replacement starts
 *     replaceEnd:   number,    // exclusive end of replacement region
 *   }
 *  OR
 *   { isReminder: true, kind: 'undated',
 *     intentMarker: string,
 *     restText: string,
 *     recurrence: null,
 *     confidence: 'medium' }
 */
export function parseReminderLine(text, opts = {}) {
  const now = opts.now || new Date();
  const trimmed = (text || '').trim();
  if (!trimmed) return { isReminder: false, kind: null, reason: 'empty' };

  // §5h — a line that ends with "?" is a question, never a reminder…
  // EXCEPT when the caller has already declared intent. Walter 2026-06-09:
  // "everything is a reminder unless it's a comment under a reminder;
  // question marks or any other characters don't change that." In a
  // DESIGNATED reminder list (or an explicit `reminder:`/`todo:` line)
  // the user has opted in, so a trailing "?" must NOT block dating —
  // "Wed Greg: dinner?" should still fire on Wednesday. Outside those
  // contexts (a normal note) the question guard stays, so "is Wed ok?"
  // never auto-captures. Callers pass opts.allowQuestion for the
  // opted-in paths.
  if (!opts.allowQuestion && /\?\s*$/.test(trimmed)) {
    return { isReminder: false, kind: null, reason: 'question' };
  }

  // --- Path B: intent markers (cheap, check first) ----------------------
  for (const re of INTENT_MARKERS) {
    const m = trimmed.match(re);
    if (m) {
      const restText = trimmed.slice(m[0].length).trim();
      if (!restText) {
        return { isReminder: false, kind: null, reason: 'no-text-after-date' };
      }
      return {
        isReminder: true,
        kind: 'undated',
        intentMarker: m[0].trim(),
        restText,
        recurrence: null,
        confidence: 'medium',
      };
    }
  }

  // --- Path A: dated / recurring reminder via chrono + recurrence -------
  if (!chrono) {
    return { isReminder: false, kind: null, reason: 'chrono-missing' };
  }

  // Pre-process: normalise listicity vocabulary (§5a-g) so chrono and
  // matchRecurrencePrefix see plain phrasing. Everything below works in
  // PRE-PROCESSED coordinates (the *P suffix); mapToOriginal() converts
  // the indices that leave this function back to the original line.
  const { text: prep, mapToOriginal } = preprocess(trimmed, now);

  // 1) Recurrence prefix.
  const recMatch = matchRecurrencePrefix(prep);
  let recurrence = recMatch ? recMatch.recurrence : null;
  let cursor = recMatch ? recMatch.matchEnd : 0;
  let replaceStartP = recMatch ? 0 : -1; // tentatively, pre-processed
  let replaceEndP = recMatch ? recMatch.matchEnd : 0;

  // Skip whitespace after recurrence (if any).
  while (prep[cursor] === ' ' || prep[cursor] === '\t') cursor++;

  // 2) Date phrase via chrono.
  //
  // If the recurrence was a single-day "every Monday" variant, chrono
  // needs to see the day-name to compute the first occurrence. We
  // start chrono just after the recurrence prefix so the literal day
  // name is in its input.
  let chronoIndexP = -1; // in pre-processed text
  let chronoText = '';
  let chronoStart = null;
  if (recMatch && recMatch.keepDayName) {
    const chronoStartIdx = recMatch.dayChronoOffset || 'every '.length;
    const sub = prep.slice(chronoStartIdx);
    const r = chrono.parse(sub, now, { forwardDate: true });
    if (r && r.length > 0 && r[0].index === 0) {
      chronoIndexP = chronoStartIdx + r[0].index;
      chronoText = r[0].text;
      chronoStart = r[0].start;
      cursor = chronoIndexP + chronoText.length;
      replaceEndP = cursor;
    }
  } else {
    // Walter 2026-09-26: an ANCHOR WORD between the rule and its start date -
    // "every Mon and Thu starting 10/1 at 9am". That is the canonical form we
    // now WRITE, so the parser has to read it back or the line would re-tidy
    // for ever. The skip is for chrono's benefit only: `cursor` moves solely
    // when a date is actually found there, so "every Mon starting the project"
    // keeps the word "starting" in its task text.
    const anchorM = recMatch ? prep.slice(cursor).match(ANCHOR_WORD_RE) : null;
    const anchorLen = anchorM ? anchorM[0].length : 0;
    const sub = prep.slice(cursor + anchorLen);
    const r = chrono.parse(sub, now, { forwardDate: true });
    if (r && r.length > 0) {
      // §5e — embedded date detection. Without a recurrence prefix the
      // date may sit ANYWHERE in the line, not just the leading window.
      // Accept the first hit that is either inside the leading window
      // OR confident about a calendar component / carries a date word
      // — so a stray low-certainty number deep in prose doesn't
      // masquerade as a date.
      let chosen = null;
      for (const cand of r) {
        if (recMatch) {
          // With a recurrence prefix the date must follow immediately.
          if (cand.index === 0) chosen = cand;
          break;
        }
        const txt = cand.text || '';
        const dateWord = /\b(to(day|night|morrow)|yesterday|mon|tue|wed|thu|fri|sat|sun|jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec|next|last|week|month|year|quarter|day)/i.test(txt);
        const confident = cand.start.isCertain('hour')
          || cand.start.isCertain('weekday')
          || cand.start.isCertain('day')
          || cand.start.isCertain('month')
          || dateWord;
        if (cand.index <= LEADING_DATE_WINDOW || confident) { chosen = cand; break; }
      }
      if (chosen) {
        chronoIndexP = cursor + anchorLen + chosen.index;
        chronoText = chosen.text;
        chronoStart = chosen.start;
        cursor = chronoIndexP + chronoText.length;
        replaceEndP = cursor;
        if (!recMatch) {
          // Replacement region is the date phrase itself — any task
          // text before AND after it is preserved into restText.
          replaceStartP = chronoIndexP;
        }
      }
    }
  }

  // No recurrence AND no date found → not a reminder (Path A fails).
  if (!recMatch && chronoIndexP < 0) {
    return { isReminder: false, kind: null, reason: 'no-match' };
  }

  // Skip whitespace.
  while (prep[cursor] === ' ' || prep[cursor] === '\t') cursor++;

  // 3) End-condition suffix (only meaningful when recurrence is set).
  if (recurrence) {
    const ec = matchEndCondition(prep.slice(cursor), now);
    if (ec) {
      recurrence = { ...recurrence, ...ec.partial };
      cursor = cursor + ec.matchLength;
      replaceEndP = cursor;
      while (prep[cursor] === ' ' || prep[cursor] === '\t') cursor++;
    }
  }

  if (replaceStartP < 0) replaceStartP = 0;

  // 4) Map the consumed [replaceStartP, replaceEndP) span back to the
  //    ORIGINAL line. restText is the original line with that span
  //    removed — for an embedded date the prefix before it is kept,
  //    so "meeting with Dan tomorrow 3pm lobby" → "meeting with Dan
  //    lobby". For a leading date the prefix is empty (unchanged
  //    behaviour). A connector word left dangling at the end of the
  //    prefix ("email Liz the review by" + "Friday") is dropped. The
  //    leading colon strip handles a user-typed "5/23: task".
  const replaceStart = mapToOriginal(replaceStartP, 'start');
  const replaceEnd = mapToOriginal(replaceEndP, 'end');
  const beforeDate = trimmed.slice(0, replaceStart)
    .replace(/[\s,]+(?:by|on|at|due|before|after|until)\s*$/i, '');
  const restText = `${beforeDate} ${trimmed.slice(replaceEnd)}`
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^[:]\s*/, '');

  // No task text left after the date/recurrence → normally not a
  // reminder. EXCEPT when the caller opts in (opts.allowEmptyBody) — in a
  // designated reminder list a bare date ("6/6") IS a reminder: Walter
  // 2026-06-09 "make it a dated entry with no body." We keep going with
  // an empty restText so it lands on that day in the agenda, bodyless.
  if (restText.length === 0 && !opts.allowEmptyBody) {
    return { isReminder: false, kind: null, reason: 'no-text-after-date' };
  }

  // 5) Compute parsedDate when chrono didn't supply one (multi-day
  //    recurrence or "every weekday" with no time-of-day).
  let parsedDate = chronoStart ? chronoStart.date() : null;
  let hasCertainTime = chronoStart ? chronoStart.isCertain('hour') : false;
  if (!parsedDate && recurrence) {
    parsedDate = computeFirstOccurrence(recurrence, now);
  } else if (recurrence && parsedDate && recurrence.byDay && recurrence.byDay.length > 1 && !chronoStart?.isCertain('weekday')) {
    // chrono gave us a date (e.g. "9am" => today 9am), but the rule
    // is multi-day — snap to the next matching weekday at the time
    // chrono parsed (or default).
    parsedDate = snapToNextByDay(parsedDate, recurrence.byDay, now);
  } else if (recurrence && parsedDate && recurrence.byMonthDay && !chronoStart?.isCertain('day')) {
    // Walter 2026-09-23: the same snap for DAY-OF-MONTH rules, which never
    // had one. "every 4th at 8am" typed on the 23rd stored tomorrow 8am —
    // chrono's reading of "8am" — a day the rule doesn't name at all, so the
    // first fire landed on the wrong day. Only when chrono was NOT certain of
    // a day: "every 4th, starting 10/14 at 8am" keeps the day he typed.
    const days = byMonthDayArray(recurrence.byMonthDay);
    if (days.length > 0 && !days.includes(parsedDate.getDate())) {
      const h = parsedDate.getHours();
      const mi = parsedDate.getMinutes();
      let soonest = null;
      for (const d of days) {
        const cand = nthDayOfMonth(parsedDate.getFullYear(), parsedDate.getMonth(), d, h, mi, now);
        if (cand && (!soonest || cand < soonest)) soonest = cand;
      }
      if (soonest) parsedDate = soonest;
    }
  }

  // Walter 2026-05-12: bare WEEKLY / MONTHLY rules are ambiguous —
  // the user typed "every week" or "every month" but didn't say WHICH
  // day. Infer from the first occurrence so the rule is self-
  // describing on writeback ("every Tue at 8am" instead of "every
  // week at 8am"; "every 23rd at 8am" instead of "every month").
  // The fire engine already treated bare WEEKLY as "every 7 days
  // from first occurrence" (same weekday) and bare MONTHLY as "same
  // calendar day each month", so this is a display/clarity fix, not
  // a behavioural change for existing reminders. interval > 1 ("every
  // 2 weeks") gets the same inference ("every 2 weeks on Tue").
  if (recurrence && parsedDate instanceof Date && !isNaN(parsedDate.getTime())) {
    if (recurrence.freq === 'WEEKLY' && !recurrence.byDay) {
      const DOW_BY_INDEX = ['SU','MO','TU','WE','TH','FR','SA'];
      recurrence = { ...recurrence, byDay: [DOW_BY_INDEX[parsedDate.getDay()]] };
    } else if (recurrence.freq === 'MONTHLY' && !recurrence.byMonthDay && !recurrence.bySetPos) {
      recurrence = { ...recurrence, byMonthDay: parsedDate.getDate() };
    }
  }

  // 6) Confidence (mostly for capture-flow's banner formatting).
  let confidence = 'medium';
  if (chronoStart) {
    const hasDate = chronoStart.isCertain('day') || chronoStart.isCertain('weekday');
    if (hasCertainTime && hasDate) confidence = 'high';
    else if (!hasDate && !hasCertainTime) confidence = 'low';
  } else if (recurrence) {
    confidence = 'high'; // recurrence rule alone is a strong signal
  }

  // 7) dateText / dateIndex report the ORIGINAL substring the user
  //    typed (e.g. "tomorrow 3pm") so the canonical bell-line composed
  //    downstream by buildResolvedDatePhrase reads naturally. When the
  //    date phrase ORIGINATES inside a pre-processed rewrite (e.g.
  //    "tonight" → "today 8pm", "every morning" → "every day 9am"),
  //    there's no clean original sub-phrase — dateText is left empty
  //    and the bell-line falls back to the resolved numeric date.
  const dateIndex = chronoIndexP >= 0 ? mapToOriginal(chronoIndexP, 'start') : 0;
  const startedInsideRewrite = chronoIndexP >= 0
    && mapToOriginal(chronoIndexP, 'start') !== mapToOriginal(chronoIndexP, 'end');
  const dateText = (chronoIndexP >= 0 && !startedInsideRewrite)
    ? trimmed.slice(dateIndex, mapToOriginal(chronoIndexP + chronoText.length, 'end'))
    : '';

  // 8) yearImputed — true when the resolved date lands in a non-current
  //    year that the user did NOT type (chrono rolled a past M/D forward
  //    a year). Capture uses this to confirm far-future dates so a stale
  //    "5/30" doesn't silently become next May. Recurrence rules don't
  //    carry a meaningful year, so they're never flagged. Walter #70.
  const userTypedYear = /\b\d{4}\b/.test(dateText) || /'\d{2}\b/.test(dateText);
  const yearImputed = !recurrence
    && parsedDate instanceof Date && !isNaN(parsedDate.getTime())
    && parsedDate.getFullYear() !== now.getFullYear()
    && !userTypedYear;

  return {
    isReminder: true,
    kind: 'dated',
    parsedDate,
    dateText,
    dateIndex,
    hasCertainTime,
    // Walter 2026-09-26: did he type an actual calendar date? isCertain('day')
    // is day-of-month, so "every Wed" (a certain WEEKDAY) is false while
    // "every Wed 10/1" is true - exactly the case where a recurring line must
    // show "starting 10/1" instead of silently swallowing what he typed.
    hasCertainDay: chronoStart ? chronoStart.isCertain('day') : false,
    restText,
    recurrence,
    confidence,
    yearImputed,
    replaceStart,
    replaceEnd,
  };
}

// ─────────────────────────────────────────────────────────────────────
// First-occurrence helpers (used when chrono can't pin a date itself)
// ─────────────────────────────────────────────────────────────────────

// Default time we splice in when the user didn't specify one. 7am is
// a reasonable "morning of" default.
export const DEFAULT_REMINDER_HOUR = 7;

// Walter 2026-05-28 (#9): byMonthDay accepts number OR number[]. Use
// this helper at every read site to get a uniform array form. The
// underlying doc is allowed to keep the scalar shape for back-compat,
// so callers don't see a runtime type change after the migration.
export function byMonthDayArray(v) {
  if (typeof v === 'number') return [v];
  if (Array.isArray(v)) return v.filter(n => typeof n === 'number' && n >= 1 && n <= 31);
  return [];
}

/**
 * dateMatchesRule(date, rec) — true when `date` is a valid occurrence
 * of `rec`. Used by the popover to decide whether changing the rule
 * should auto-bump the parsedDate to the next matching instance.
 *
 *   WEEKLY rule with byDay → check date's weekday ∈ byDay.
 *   MONTHLY byMonthDay   → check date.getDate() === byMonthDay.
 *   MONTHLY bySetPos + 1-day byDay → check date is the Nth weekday.
 *   DAILY / generic / YEARLY → any date matches (rule has no
 *                              calendar-specific constraint).
 *
 * Walter 2026-05-12: needed so "change rule from every Mon to every
 * Tue" forces the date forward instead of leaving it on a Monday.
 */
export function dateMatchesRule(date, rec) {
  if (!(date instanceof Date) || isNaN(date.getTime())) return false;
  const r = normalizeRecurrence(rec);
  if (!r) return false;
  const dowCodes = ['SU','MO','TU','WE','TH','FR','SA'];
  switch (r.freq) {
    case 'DAILY':
      return true;
    case 'WEEKLY':
      if (!Array.isArray(r.byDay) || r.byDay.length === 0) return true;
      return r.byDay.includes(dowCodes[date.getDay()]);
    case 'MONTHLY': {
      // Walter 2026-05-28 (#9): byMonthDay can be number or number[].
      const days = byMonthDayArray(r.byMonthDay);
      if (days.length > 0) {
        return days.includes(date.getDate());
      }
      if (r.bySetPos != null && Array.isArray(r.byDay) && r.byDay.length === 1) {
        if (dowCodes[date.getDay()] !== r.byDay[0]) return false;
        if (r.bySetPos === -1) {
          // "last <weekday> of the month": no later same-weekday in this month.
          const next = new Date(date);
          next.setDate(date.getDate() + 7);
          return next.getMonth() !== date.getMonth();
        }
        const nth = Math.ceil(date.getDate() / 7);
        return nth === r.bySetPos;
      }
      return true;
    }
    case 'YEARLY':
      return true;
    default:
      return false;
  }
}

/**
 * computeFirstOccurrence(rec, now) — first time the rule fires after
 * `now`. Defaults the time-of-day to DEFAULT_REMINDER_HOUR (7am). Used
 * by the inline parser when no explicit chrono date is present, and
 * by the popover when the user changes the rule and the existing
 * date no longer matches (auto-bump to the next matching occurrence).
 */
export function computeFirstOccurrence(rec, now) {
  const r = normalizeRecurrence(rec);
  if (!r) return null;
  const start = new Date(now);
  start.setHours(DEFAULT_REMINDER_HOUR, 0, 0, 0);
  // If it's already past 7am today, push to tomorrow as the baseline.
  if (start <= now) start.setDate(start.getDate() + 1);

  switch (r.freq) {
    case 'DAILY':
      return start;
    case 'WEEKLY':
      if (r.byDay && r.byDay.length > 0) {
        return snapToNextByDay(start, r.byDay, now);
      }
      return start;
    case 'MONTHLY':
      if (r.bySetPos && r.byDay && r.byDay.length === 1) {
        return nthWeekdayOfMonth(now.getFullYear(), now.getMonth(), r.bySetPos, r.byDay[0], DEFAULT_REMINDER_HOUR, 0, now);
      }
      // Walter 2026-05-28 (#9): byMonthDay can be number or number[].
      // Walter 2026-09-23: take the SOONEST of the days, which is not the
      // lowest-numbered one. nthDayOfMonth already rolls a day that has
      // passed into the next month that has it, so asking it for each day
      // and taking the minimum is the whole answer. The previous loop
      // returned the first candidate in numeric order — on the 23rd, "every
      // 4th, 14th and 24th" answered Oct 4 (day 4 rolled forward and won)
      // and the 24th, the next day on his calendar, was never considered.
      {
        const days = byMonthDayArray(r.byMonthDay);
        if (days.length > 0) {
          let soonest = null;
          for (const d of days) {
            const cand = nthDayOfMonth(now.getFullYear(), now.getMonth(), d, DEFAULT_REMINDER_HOUR, 0, now);
            if (cand && (!soonest || cand < soonest)) soonest = cand;
          }
          if (soonest) return soonest;
        }
      }
      // Generic monthly — same day-of-month next month.
      return start;
    case 'YEARLY':
      return start;
    default:
      return null;
  }
}

/**
 * computeNextOccurrence(fromDate, rec) — advance ONE interval forward
 * from `fromDate` per the rule. Time-of-day on the result matches the
 * time-of-day on `fromDate` (we just roll the date forward). Returns
 * a Date or null.
 *
 * Walter 2026-05-13: used when the user marks a recurring reminder
 * "done" — instead of killing the rule (status=done), we advance the
 * parsedDate to the next occurrence and keep status=open so the next
 * instance fires normally. Server's computeNextOccurrence in
 * functions/index.js handles the same logic with full timezone +
 * DST math; this client-side version uses local-time math because
 * the user is editing in their own timezone, which is "close enough"
 * for the optimistic UI update. The next server fire will recompute
 * from the new parsedDate via the same server logic and resync.
 *
 * Supports DAILY, WEEKLY (with or without byDay), MONTHLY (byMonthDay
 * or generic), YEARLY. bySetPos (Nth weekday of month) is not handled
 * here yet — returns null and the caller falls back to the kill-rule
 * behaviour, which is acceptable for that rare case.
 */
export function computeNextOccurrence(fromDate, rec) {
  const r = normalizeRecurrence(rec);
  if (!r) return null;
  const from = new Date(fromDate);
  if (isNaN(from.getTime())) return null;
  const interval = Math.max(1, r.interval || 1);
  const result = new Date(from);

  switch (r.freq) {
    case 'DAILY':
      result.setDate(result.getDate() + interval);
      return result;

    case 'WEEKLY': {
      if (Array.isArray(r.byDay) && r.byDay.length > 0) {
        const targets = r.byDay.map(s => DOW_NUMS[s]).filter(n => n != null);
        if (targets.length === 0) return null;
        if (interval === 1) {
          do { result.setDate(result.getDate() + 1); }
          while (!targets.includes(result.getDay()));
        } else {
          // Multi-week interval — try remaining days of current week,
          // else jump (interval - 1) weeks and pick first byDay match.
          let snapped = null;
          for (let off = 1; off < 7; off++) {
            const test = new Date(from);
            test.setDate(test.getDate() + off);
            if (targets.includes(test.getDay())) { snapped = test; break; }
          }
          if (snapped) {
            result.setTime(snapped.getTime());
          } else {
            result.setDate(result.getDate() + (interval - 1) * 7 + (7 - result.getDay()));
            for (let off = 0; off < 7; off++) {
              const test = new Date(result);
              test.setDate(test.getDate() + off);
              if (targets.includes(test.getDay())) { result.setTime(test.getTime()); break; }
            }
          }
        }
        return result;
      }
      result.setDate(result.getDate() + 7 * interval);
      return result;
    }

    case 'MONTHLY': {
      if (r.bySetPos) return null; // unsupported here, caller falls back
      // Walter 2026-05-28 (#9): byMonthDay can be a number or a
      // sorted-ascending array. For arrays, find the NEXT day-of-month
      // strictly after the current `from` day: if there's a higher day
      // in the same month, use it; otherwise jump to the next interval
      // and use the smallest day in the array. Same Feb-30-skip
      // protection as the scalar branch.
      const days = byMonthDayArray(r.byMonthDay);
      if (days.length > 0) {
        const fromDay = from.getDate();
        // First try: a same-month day strictly greater than fromDay.
        // ("every 1st and 15th", fired on the 1st → next is the 15th
        // SAME month, no month advance.)
        const sameMonth = days.find(d => d > fromDay);
        if (sameMonth != null) {
          // Same month. Validate the day exists in this month (e.g.
          // 31 in a 30-day month — fall through to next-month search).
          const daysInThisMonth = new Date(result.getFullYear(), result.getMonth() + 1, 0).getDate();
          if (sameMonth <= daysInThisMonth) {
            result.setDate(sameMonth);
            return result;
          }
        }
        // Either we exhausted same-month options OR same-month
        // candidate didn't fit (e.g. 31st in February). Jump forward
        // one interval and try days in order until we find one that
        // exists in that month. 24-iter cap.
        for (let i = 0; i < 24; i++) {
          result.setMonth(result.getMonth() + interval);
          result.setDate(1);
          const daysInMonth = new Date(result.getFullYear(), result.getMonth() + 1, 0).getDate();
          for (const d of days) {
            if (d <= daysInMonth) {
              result.setDate(d);
              return result;
            }
          }
        }
        return null;
      }
      // Generic monthly — same calendar day next month (or last valid day).
      const targetDay = from.getDate();
      result.setDate(1);
      result.setMonth(result.getMonth() + interval);
      const daysInMonth = new Date(result.getFullYear(), result.getMonth() + 1, 0).getDate();
      result.setDate(Math.min(targetDay, daysInMonth));
      return result;
    }

    case 'YEARLY':
      result.setFullYear(result.getFullYear() + interval);
      return result;

    default:
      return null;
  }
}

// Snap `from` forward to the next occurrence of any weekday in
// `byDayArr`. Time-of-day on the returned Date is taken from `from`.
// `now` is the reference "now"; the snap result is always > now.
function snapToNextByDay(from, byDayArr, now) {
  const targets = byDayArr.map(d => DOW_NUMS[d]).filter(n => n != null);
  if (targets.length === 0) return null;
  const start = new Date(from);
  // Try today first; if it's a target day AND start > now, use today.
  for (let i = 0; i < 7; i++) {
    const dow = start.getDay();
    if (targets.includes(dow) && start > now) return start;
    start.setDate(start.getDate() + 1);
  }
  return start;
}

// Compute the date of the Nth weekday of a given month (1-indexed
// "first", "second", ...; -1 = "last"). Returns null if no such date.
// Time-of-day is set from hour/min. If the resulting date is in the
// past relative to `now`, rolls forward by months until it isn't.
function nthWeekdayOfMonth(year, month, pos, dowShort, hour, min, now) {
  const targetDow = DOW_NUMS[dowShort];
  if (targetDow == null) return null;

  const tryMonth = (y, m) => {
    if (pos > 0) {
      const first = new Date(y, m, 1, hour, min, 0, 0);
      const firstDow = first.getDay();
      const dayOfMonth = 1 + ((targetDow - firstDow + 7) % 7) + (pos - 1) * 7;
      if (dayOfMonth > daysInMonth(y, m)) return null;
      return new Date(y, m, dayOfMonth, hour, min, 0, 0);
    }
    // Last occurrence: walk back from last day of month.
    const lastDay = daysInMonth(y, m);
    const last = new Date(y, m, lastDay, hour, min, 0, 0);
    const lastDow = last.getDay();
    const dayOfMonth = lastDay - ((lastDow - targetDow + 7) % 7);
    return new Date(y, m, dayOfMonth, hour, min, 0, 0);
  };

  let y = year, m = month;
  for (let i = 0; i < 24; i++) {
    const d = tryMonth(y, m);
    if (d && d > now) return d;
    m++;
    if (m > 11) { m = 0; y++; }
  }
  return null;
}

// Compute the date of the Nth day of a given month, hour/min set. If
// the month doesn't contain that day (e.g. Feb 31), skip to the next
// month that does. If the resulting date is past, roll forward.
function nthDayOfMonth(year, month, dayN, hour, min, now) {
  let y = year, m = month;
  for (let i = 0; i < 24; i++) {
    if (dayN <= daysInMonth(y, m)) {
      const d = new Date(y, m, dayN, hour, min, 0, 0);
      if (d > now) return d;
    }
    m++;
    if (m > 11) { m = 0; y++; }
  }
  return null;
}

function daysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

// ─────────────────────────────────────────────────────────────────────
// Formatters
// ─────────────────────────────────────────────────────────────────────

export function formatTimeShort(date) {
  if (!(date instanceof Date) || isNaN(date.getTime())) return '';
  const h = date.getHours();
  const min = date.getMinutes();
  const ampm = h >= 12 ? 'pm' : 'am';
  const h12 = h === 0 ? 12 : (h > 12 ? h - 12 : h);
  return min === 0 ? `${h12}${ampm}` : `${h12}:${String(min).padStart(2, '0')}${ampm}`;
}

export function formatMonthDay(date) {
  if (!(date instanceof Date) || isNaN(date.getTime())) return '';
  return `${date.getMonth() + 1}/${date.getDate()}`;
}

// Strips embedded time phrases from a date-text fragment so we can
// pull out just the "day phrase" portion.
function stripTimePhrases(s) {
  if (!s) return '';
  return s
    .replace(/\b\d{1,2}(?::\d{2})?\s*[ap]m\b/gi, '')
    .replace(/\bnoon\b/gi, '')
    .replace(/\bmidnight\b/gi, '')
    .replace(/\b\d{1,2}:\d{2}\b/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}


/**
 * buildResolvedDatePhrase — composes the canonical inline date phrase
 * that goes into the source line. Examples:
 *
 *   "Sunday call pete"      -> "Sunday 5/3 7am"
 *   "Sunday 3pm call pete"  -> "Sunday 5/3 3pm"
 *   "tomorrow call pete"    -> "tomorrow 5/3 7am"
 *
 * With recurrence: prefixes "every Mon" / "every weekday" / etc. and
 * strips the leading day-name from the day phrase if it'd duplicate
 * the byDay code. End conditions (count/until) are NOT included in
 * the inline phrase — they live in the structured doc only.
 */
export function buildResolvedDatePhrase(dateText, parsedDate, hasCertainTime, defaultHour = DEFAULT_REMINDER_HOUR, defaultMinute = 0, recurrence = null, startAnchor = false) {
  if (!(parsedDate instanceof Date) || isNaN(parsedDate.getTime())) {
    // No date — recurrence-only line. Still emit the recurrence label
    // so the source line reads as a rule.
    return recurrence ? formatRecurrenceLabel(recurrence) : (dateText || '');
  }
  const dayPhrase = stripTimePhrases(dateText || '');
  const md = formatMonthDay(parsedDate);
  // Walter 2026-07-04: ONE canonical numeric form for every bell-line date —
  // "M/D" in the current year, "M/D/YYYY" (slashed 4-digit year) otherwise.
  // Previously the phrase kept whatever the user typed for month-name forms
  // ("Nov 1", "April 15 2027") and used a space-separated / apostrophe year
  // for computed dates, so a reminder list showed three different date shapes
  // side by side. Now they all normalise to the same numeric slashed form.
  // (Recurring rules fire by weekday / month-day, so they carry no meaningful
  // year → md only.)
  const now = new Date();
  const year = parsedDate.getFullYear();
  const datePart = (recurrence || year === now.getFullYear())
    ? md
    : `${md}/${year}`;
  let timeStr;
  if (hasCertainTime) {
    timeStr = formatTimeShort(parsedDate);
  } else {
    const defaulted = new Date(parsedDate);
    defaulted.setHours(defaultHour, defaultMinute, 0, 0);
    timeStr = formatTimeShort(defaulted);
  }
  const rec = normalizeRecurrence(recurrence);
  const recPrefix = rec ? formatRecurrenceLabel(rec, { omitEndCondition: true }) : '';
  let dayPart = dayPhrase;
  // If the recurrence already names the weekday (e.g. byDay=[MO]),
  // strip any leading day-name from the user's day phrase to avoid
  // "every Mon Monday 5/4 3pm".
  if (rec && Array.isArray(rec.byDay) && rec.byDay.length > 0 && dayPart) {
    dayPart = dayPart.replace(/^(mon|tue|wed|thu|fri|sat|sun)(day|sday|nesday|rsday|urday)?\s*/i, '').trim();
  }
  // Walter 2026-05-28: strip leading connective words ("at", "on")
  // that survive after a weekday gets removed — e.g. "Friday at noon"
  // → stripTimePhrases → "Friday at" → byDay strip → "at". These
  // leftover prepositions would then appear awkwardly in the bell-line
  // ("every Fri at 5/29 12pm"). Stripping them lets the recurring
  // branch below produce the canonical "every Fri at 12pm".
  if (dayPart) {
    dayPart = dayPart.replace(/^(at|on)\b\s*/i, '').trim();
  }
  let core;
  if (rec) {
    // Walter 2026-05-28: for MOST recurring reminders the recPrefix carries
    // the full schedule ("every Fri", "every weekday", "every 15th") — the
    // specific NEXT-fire date is then redundant AND misleading (reads like a
    // one-off: "every Fri at 5/29 12pm"). So we show only the time.
    //
    // Walter 2026-07-04 EXCEPTION: a YEARLY rule carries NO month/day in the
    // schema (freq=YEARLY, no byMonth field) — the anchor lives only in the
    // fire date. Dropping it turned "annual oct 1 do that" into "every year
    // at 7am" with the day gone. So for yearly we KEEP the M/D. The line
    // still round-trips ("every year 10/1 7am" re-parses to YEARLY on Oct 1).
    // Walter 2026-09-26: when he TYPED a start date, say so. He typed
    // "every thu and mon 10/1 9am book squash II" and the tidy-up wrote
    // "every Mon and Thu at 9am: book squash II" - the 10/1 was used (it is
    // the first fire) but deleted from his line, so the line no longer showed
    // how it had been read. His call, reversing the 2026-07 removal of the
    // "starting 5/18" suffix, and narrower than that one was: the clause
    // appears ONLY when he typed a calendar date, so "every Wed call mom"
    // stays clean. Round-trips via ANCHOR_WORD_RE above.
    if (rec.freq === 'YEARLY') {
      core = `${formatMonthDay(parsedDate)} ${timeStr}`;
    } else if (startAnchor) {
      // Walter 2026-09-26 (#246): `startAnchor` is either `true` - "the date I
      // just handed you IS the start", the typing path - or the STORED start
      // date, for a line rebuilt from the reminder record. The stored form
      // matters because parsedDate is the NEXT fire and moves on; the clause
      // must keep naming the day the series began.
      const anchorDate = startAnchor instanceof Date ? startAnchor : parsedDate;
      core = `starting ${formatMonthDay(anchorDate)} at ${timeStr}`;
    } else {
      core = `at ${timeStr}`;
    }
  } else {
    // Walter 2026-07-04: EVERY resolved one-off date normalises to the same
    // canonical numeric form — whether the user typed a bare "5/12", a full
    // "8/31/2027", a verbal "April 15", a weekday ("Fri"), or a relative
    // phrase ("tomorrow"). We drop the typed wording and emit `datePart`
    // (M/D, or M/D/YYYY cross-year). This supersedes the older "keep the
    // month-name / their exact slash-year" special cases that left a reminder
    // list showing three different date shapes at once. (A verbal/weekday
    // word is also redundant with the date and goes stale on later edits.)
    core = `${datePart} ${timeStr}`;
  }
  return recPrefix ? `${recPrefix} ${core}` : core;
}

/**
 * Legacy alias — older capture flow signature.
 */
export function formatResolvedDateTime(parsedDate, hasCertainTime) {
  return buildResolvedDatePhrase('', parsedDate, hasCertainTime);
}

/**
 * formatRecurrenceLabel(rec, opts?) — short human-readable form of a
 * recurrence rule. Normalises legacy and new shapes via
 * normalizeRecurrence first.
 *
 *   { freq: 'DAILY' }                  -> "every day"
 *   { freq: 'DAILY', interval: 3 }     -> "every 3 days"
 *   { freq: 'WEEKLY', byDay: ['TH'] }  -> "every Thu"
 *   { freq: 'WEEKLY', byDay: ['MO','TU','WE','TH','FR'] } -> "every weekday"
 *   { freq: 'WEEKLY', byDay: ['MO','WE','FR'] } -> "every Mon/Wed/Fri"
 *   { freq: 'MONTHLY', bySetPos: 1, byDay: ['TU'] } -> "every 1st Tuesday"
 *   { freq: 'MONTHLY', bySetPos: -1, byDay: ['FR'] } -> "every last Friday"
 *   { freq: 'MONTHLY', byMonthDay: 15 } -> "every 15th"
 *
 * With opts.omitEndCondition, count/until are suppressed (used by
 * buildResolvedDatePhrase since we don't want "× 6" in the source line).
 */
export function formatRecurrenceLabel(rec, opts = {}) {
  const r = normalizeRecurrence(rec);
  if (!r) return '';
  const interval = r.interval || 1;
  let core = '';
  switch (r.freq) {
    case 'DAILY':
      core = interval > 1 ? `every ${interval} days` : 'every day';
      break;
    case 'WEEKLY':
      if (Array.isArray(r.byDay) && r.byDay.length > 0) {
        if (sameSet(r.byDay, ['MO','TU','WE','TH','FR'])) {
          core = 'every weekday';
        } else if (r.byDay.length === 1) {
          const name = DOW_LABELS_SHORT[r.byDay[0]] || r.byDay[0];
          core = interval > 1 ? `every ${interval} wks on ${name}` : `every ${name}`;
        } else {
          // Walter 2026-05-11: use "Mon and Wed" for 2 days, "Mon,
          // Wed and Fri" for 3+ — reads as English prose, matches
          // the canonical-form decision. Previous "/"-separated form
          // ("Mon/Wed/Fri") felt machine-y.
          const ordered = orderByDay(r.byDay).map(d => DOW_LABELS_SHORT[d] || d);
          let list;
          if (ordered.length === 2) {
            list = `${ordered[0]} and ${ordered[1]}`;
          } else {
            list = `${ordered.slice(0, -1).join(', ')} and ${ordered[ordered.length - 1]}`;
          }
          core = interval > 1 ? `every ${interval} wks on ${list}` : `every ${list}`;
        }
      } else {
        core = interval > 1 ? `every ${interval} weeks` : 'every week';
      }
      break;
    case 'MONTHLY':
      if (r.bySetPos && Array.isArray(r.byDay) && r.byDay.length === 1) {
        const posWord = posLabel(r.bySetPos);
        const name = DOW_LABELS_LONG[r.byDay[0]] || r.byDay[0];
        core = `every ${posWord} ${name}`;
      } else if (byMonthDayArray(r.byMonthDay).length > 1) {
        // Walter 2026-05-28 (#9): multi-day form — "every 1st and 15th"
        // / "every 1st, 15th and 30th". Same and-with-Oxford-list
        // convention as multi-byDay above.
        const ords = byMonthDayArray(r.byMonthDay).map(ordinalSuffix);
        let list;
        if (ords.length === 2) list = `${ords[0]} and ${ords[1]}`;
        else list = `${ords.slice(0, -1).join(', ')} and ${ords[ords.length - 1]}`;
        core = `every ${list}`;
      } else if (typeof r.byMonthDay === 'number') {
        core = `every ${ordinalSuffix(r.byMonthDay)}`;
      } else {
        core = interval > 1 ? `every ${interval} months` : 'every month';
      }
      break;
    case 'YEARLY':
      core = interval > 1 ? `every ${interval} years` : 'every year';
      break;
    default:
      return '';
  }
  if (opts.omitEndCondition) return core;
  let suffix = '';
  if (typeof r.count === 'number') suffix = ` · ×${r.count}`;
  else if (typeof r.until === 'string') suffix = ` · until ${shortUntil(r.until)}`;
  return core + suffix;
}

function shortUntil(iso) {
  // "2026-05-31" -> "May 31". Best-effort; falls back to the raw ISO.
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return iso;
  const d = new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10));
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function sameSet(a, b) {
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;
  const sa = new Set(a), sb = new Set(b);
  for (const x of sa) if (!sb.has(x)) return false;
  return true;
}

function orderByDay(arr) {
  return arr.slice().sort((a, b) => DOW_SHORT.indexOf(a) - DOW_SHORT.indexOf(b));
}

function posLabel(n) {
  if (n === -1) return 'last';
  if (n === 1) return '1st';
  if (n === 2) return '2nd';
  if (n === 3) return '3rd';
  if (n === 4) return '4th';
  if (n === 5) return '5th';
  return String(n);
}

function ordinalSuffix(n) {
  const v = n % 100;
  if (v >= 11 && v <= 13) return `${n}th`;
  switch (n % 10) {
    case 1: return `${n}st`;
    case 2: return `${n}nd`;
    case 3: return `${n}rd`;
    default: return `${n}th`;
  }
}

/**
 * formatRecurrenceAsNL(rec) — emit the canonical natural-language form
 * of a recurrence rule. Designed so that `parseRecurrenceText(formatRecurrenceAsNL(r))`
 * round-trips back to the same shape.
 *
 *   null                                 -> ""
 *   {DAILY, 1}                           -> "every day"
 *   {DAILY, 3}                           -> "every 3 days"
 *   {WEEKLY, byDay:[MO]}                 -> "every Monday"
 *   {WEEKLY, byDay:[MO,WE,FR]}           -> "every Mon, Wed, Fri"
 *   {WEEKLY, byDay:[MO..FR]}             -> "every weekday"
 *   {WEEKLY, interval:2, byDay:[TU]}     -> "every 2 weeks on Tuesday"
 *   {MONTHLY, byMonthDay:15}             -> "every 15th of the month"
 *   {MONTHLY, bySetPos:1, byDay:[TU]}    -> "every first Tuesday of the month"
 *   {MONTHLY, bySetPos:-1, byDay:[FR]}   -> "every last Friday of the month"
 *   {YEARLY, 1}                          -> "every year"
 *
 * End conditions append:
 *   count:6                              -> " for 6 occurrences"
 *   until:'2027-08-31'                   -> " until Aug 31 2027"
 *
 * Used by the RecurrencePicker's NL field as the canonical display
 * form, and by the picker's two-way sync with the dropdown.
 */
export function formatRecurrenceAsNL(rec) {
  const r = normalizeRecurrence(rec);
  if (!r) return '';
  const interval = r.interval || 1;
  let core = '';
  switch (r.freq) {
    case 'DAILY':
      core = interval === 1 ? 'every day' : `every ${interval} days`;
      break;
    case 'WEEKLY':
      if (Array.isArray(r.byDay) && r.byDay.length > 0) {
        if (sameSet(r.byDay, ['MO','TU','WE','TH','FR'])) {
          core = 'every weekday';
        } else if (r.byDay.length === 1) {
          const name = DOW_LABELS_LONG[r.byDay[0]] || r.byDay[0];
          core = interval > 1 ? `every ${interval} weeks on ${name}` : `every ${name}`;
        } else {
          // Walter 2026-05-11: "Mon and Wed" / "Mon, Wed and Fri" —
          // English prose, not slash-separated.
          const ordered = orderByDay(r.byDay).map(d => DOW_LABELS_SHORT[d] || d);
          let list;
          if (ordered.length === 2) {
            list = `${ordered[0]} and ${ordered[1]}`;
          } else {
            list = `${ordered.slice(0, -1).join(', ')} and ${ordered[ordered.length - 1]}`;
          }
          core = interval > 1 ? `every ${interval} weeks on ${list}` : `every ${list}`;
        }
      } else {
        core = interval === 1 ? 'every week' : `every ${interval} weeks`;
      }
      break;
    case 'MONTHLY':
      if (r.bySetPos != null && Array.isArray(r.byDay) && r.byDay.length === 1) {
        const pos = SETPOS_WORDS[r.bySetPos] || 'first';
        const name = DOW_LABELS_LONG[r.byDay[0]] || r.byDay[0];
        core = `every ${pos} ${name} of the month`;
      } else if (byMonthDayArray(r.byMonthDay).length > 1) {
        const ords = byMonthDayArray(r.byMonthDay).map(ordinalSuffix);
        let list;
        if (ords.length === 2) list = `${ords[0]} and ${ords[1]}`;
        else list = `${ords.slice(0, -1).join(', ')} and ${ords[ords.length - 1]}`;
        core = `every ${list} of the month`;
      } else if (typeof r.byMonthDay === 'number') {
        core = `every ${ordinalSuffix(r.byMonthDay)} of the month`;
      } else {
        core = interval === 1 ? 'every month' : `every ${interval} months`;
      }
      break;
    case 'YEARLY':
      core = interval === 1 ? 'every year' : `every ${interval} years`;
      break;
    default:
      return '';
  }
  let suffix = '';
  if (typeof r.count === 'number') {
    suffix = ` for ${r.count} ${r.count === 1 ? 'occurrence' : 'occurrences'}`;
  } else if (typeof r.until === 'string') {
    const m = r.until.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (m) {
      const d = new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10));
      if (!isNaN(d.getTime())) {
        suffix = ` until ${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
      }
    }
  }
  return core + suffix;
}

const SETPOS_WORDS = { 1: 'first', 2: 'second', 3: 'third', 4: 'fourth', 5: 'fifth', '-1': 'last' };

/**
 * parseRecurrenceText(text) — parse a standalone recurrence phrase
 * (NO task text following). Returns { recurrence, valid }.
 *
 * Used by the RecurrencePicker's NL field. Empty/whitespace input
 * is valid and maps to "no recurrence" (null).
 *
 * Walter 2026-05-11: distinct from parseReminderLine because that
 * function REQUIRES task text after the date/recurrence (returns
 * 'no-text-after-date' otherwise). The picker's NL field is
 * recurrence-only by design.
 */
export function parseRecurrenceText(text, opts = {}) {
  const now = opts.now || new Date();
  const trimmed = (text || '').trim();
  if (!trimmed) return { recurrence: null, valid: true };
  const recMatch = matchRecurrencePrefix(trimmed);
  if (!recMatch) return { recurrence: null, valid: false };
  let cursor = recMatch.matchEnd;
  while (trimmed[cursor] === ' ' || trimmed[cursor] === '\t') cursor++;
  let recurrence = recMatch.recurrence;
  if (cursor < trimmed.length) {
    const ec = matchEndCondition(trimmed.slice(cursor), now);
    if (ec) {
      recurrence = { ...recurrence, ...ec.partial };
      cursor += ec.matchLength;
      while (trimmed[cursor] === ' ' || trimmed[cursor] === '\t') cursor++;
    }
  }
  if (cursor < trimmed.length) {
    // Trailing junk we couldn't account for.
    return { recurrence: null, valid: false };
  }
  return { recurrence, valid: true };
}

/**
 * parseReminderParagraph(paragraph)
 *
 * First line of a paragraph is the reminder phrase; subsequent lines
 * (until blank) are notes attached to it.
 */
export function parseReminderParagraph(paragraph, opts) {
  const lines = (paragraph || '').split('\n');
  const phrase = (lines[0] || '').trim();
  const notes  = lines.slice(1).join('\n').trim();
  return {
    phrase,
    notes,
    parse: parseReminderLine(phrase, opts),
  };
}

// ─────────────────────────────────────────────────────────────────────
// Log lines (unchanged)
// ─────────────────────────────────────────────────────────────────────

export function parseLogLine(text, opts = {}) {
  const now = opts.now || new Date();
  const trimmed = (text || '').trim();
  if (!trimmed) return { isLogged: false, reason: 'empty' };
  if (!trimmed.startsWith('@')) return { isLogged: false, reason: 'no-marker' };
  const afterAt = trimmed.slice(1);
  if (!afterAt || /^\s/.test(afterAt)) {
    return { isLogged: false, reason: 'no-content' };
  }

  const relMatch = afterAt.match(/^(today|yesterday|tomorrow)\b/i);
  if (relMatch) {
    const phrase = relMatch[1].toLowerCase();
    const d = new Date(now);
    d.setHours(0, 0, 0, 0);
    if (phrase === 'yesterday') d.setDate(d.getDate() - 1);
    else if (phrase === 'tomorrow') d.setDate(d.getDate() + 1);
    return {
      isLogged: true,
      parsedDate: d,
      dateText: relMatch[0],
      dateIndex: 1,
      hasCertainTime: false,
      restText: afterAt.slice(relMatch[0].length).trim(),
      recurrence: null,
      relative: phrase,
    };
  }

  // Weekdays — full names, 3-letter (`mon`/`wed`/...), and 2-letter
  // (`mo`/`we`/`th`/...). Resolves to the MOST RECENT past occurrence
  // (today if today matches): log entries are typically about events
  // that already happened.
  // Walter 2026-05-25 (Bug #8 — round-2 tester report): `@today` was
  // the only @-shortcut recognised; `@Sunday` / `@Friday` etc. fell
  // through to chrono and failed. Same 2-letter forms as the reminder
  // parser's DOW_TOKENS, so the two surfaces stay in sync.
  const wdMatch = afterAt.match(/^(monday|tuesday|wednesday|thursday|friday|saturday|sunday|mon|tue|wed|thu|fri|sat|sun|mo|tu|we|th|fr|sa|su)\b/i);
  if (wdMatch) {
    const tok = wdMatch[1].toLowerCase();
    const wdToIdx = {
      monday: 1, mon: 1, mo: 1,
      tuesday: 2, tue: 2, tu: 2,
      wednesday: 3, wed: 3, we: 3,
      thursday: 4, thu: 4, th: 4,
      friday: 5, fri: 5, fr: 5,
      saturday: 6, sat: 6, sa: 6,
      sunday: 0, sun: 0, su: 0,
    };
    const targetIdx = wdToIdx[tok];
    const d = new Date(now);
    d.setHours(0, 0, 0, 0);
    const currIdx = d.getDay();
    // Most recent past occurrence (today if currIdx === targetIdx).
    const diff = (currIdx - targetIdx + 7) % 7;
    if (diff > 0) d.setDate(d.getDate() - diff);
    return {
      isLogged: true,
      parsedDate: d,
      dateText: wdMatch[0],
      dateIndex: 1,
      hasCertainTime: false,
      restText: afterAt.slice(wdMatch[0].length).trim(),
      recurrence: null,
      relative: tok,
    };
  }

  if (!chrono) {
    return { isLogged: false, reason: 'chrono-missing' };
  }

  const results = chrono.parse(afterAt, now, { forwardDate: false });
  if (!results || results.length === 0) {
    return { isLogged: false, reason: 'no-match' };
  }
  const first = results[0];
  if (first.index > 0) {
    return { isLogged: false, reason: 'date-not-at-start' };
  }
  const afterDate = afterAt.slice(first.index + first.text.length).trim();
  return {
    isLogged: true,
    parsedDate: first.start.date(),
    dateText: first.text,
    dateIndex: 1 + first.index,
    hasCertainTime: first.start.isCertain('hour'),
    restText: afterDate,
    recurrence: null,
    relative: null,
  };
}

export function formatLogDate(date) {
  if (!(date instanceof Date) || isNaN(date.getTime())) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function rewriteRelativeLogDates(line, now = new Date()) {
  if (!line || !line.includes('@')) return line;
  return line.replace(/@(today|yesterday|tomorrow)\b/gi, (_, phrase) => {
    const p = phrase.toLowerCase();
    const d = new Date(now);
    d.setHours(0, 0, 0, 0);
    if (p === 'yesterday') d.setDate(d.getDate() - 1);
    else if (p === 'tomorrow') d.setDate(d.getDate() + 1);
    return '@' + formatLogDate(d);
  });
}

export function splitReminderListIntoParagraphs(text) {
  if (!text) return [];
  return text
    .split(/\n\s*\n/)
    .map(p => p.replace(/\s+$/, ''))
    .filter(p => p.trim().length > 0);
}
